/**
 * Utility functions for handling API errors
 */

/**
 * Extract string message from various message formats
 * @param {any} message - The message to extract string from
 * @returns {string} - Clean string message
 */
export const extractMessage = (message) => {
  // Handle null/undefined
  if (message === null || message === undefined) {
    return "خطایی رخ داده است.";
  }

  // Already a string
  if (typeof message === "string") {
    return message.trim() || "خطایی رخ داده است.";
  }

  // Handle objects
  if (typeof message === "object") {
    // Handle common object formats that servers might send
    if (typeof message.text === "string" && message.text.trim()) {
      return message.text.trim();
    }
    if (typeof message.message === "string" && message.message.trim()) {
      return message.message.trim();
    }
    if (typeof message.error === "string" && message.error.trim()) {
      return message.error.trim();
    }
    if (typeof message.description === "string" && message.description.trim()) {
      return message.description.trim();
    }
    if (typeof message.content === "string" && message.content.trim()) {
      return message.content.trim();
    }
    if (typeof message.value === "string" && message.value.trim()) {
      return message.value.trim();
    }

    // Handle nested text objects recursively
    if (message.text && typeof message.text === "object") {
      return extractMessage(message.text);
    }

    // Handle nested error objects
    if (message.data && typeof message.data === "object") {
      if (
        typeof message.data.message === "string" &&
        message.data.message.trim()
      ) {
        return message.data.message.trim();
      }
      if (typeof message.data.error === "string" && message.data.error.trim()) {
        return message.data.error.trim();
      }
    }

    // Special handling for specific object structures like {text, type}
    if (message.hasOwnProperty("text") && message.hasOwnProperty("type")) {
      // This is likely a structured message object
      if (typeof message.text === "string" && message.text.trim()) {
        return message.text.trim();
      }
      // If text is not a string, fallback to default
      return "خطایی رخ داده است.";
    }

    // Try to stringify if it's a plain object, but avoid circular references
    try {
      if (message.constructor === Object) {
        const stringified = JSON.stringify(message);
        return stringified.length > 200 ? "خطایی رخ داده است." : stringified;
      }
    } catch (error) {
      // Silently handle JSON.stringify errors
    }

    return "خطایی رخ داده است.";
  }

  // Handle numbers, booleans, etc.
  try {
    const stringValue = String(message);
    return stringValue.trim() || "خطایی رخ داده است.";
  } catch {
    return "خطایی رخ داده است.";
  }
};

/**
 * Ensure the message is a safe string for React components
 * @param {any} message - The message to make safe
 * @returns {string} - Safe string message
 */
export const ensureSafeString = (message) => {
  const extracted = extractMessage(message);

  // Double check to ensure we have a string
  if (typeof extracted !== "string") {
    return "خطایی رخ داده است.";
  }

  // Ensure it's not empty
  if (!extracted.trim()) {
    return "خطایی رخ داده است.";
  }

  return extracted;
};

/**
 * Check if an error is an authentication error that should be handled silently
 * @param {Error} error - The error object to check
 * @returns {boolean} - True if it's an auth error that should be ignored
 */
export const isAuthError = (error) => {
  return error?.isAuthError === true || error?.shouldRedirect === true;
};

/**
 * Extract server message specifically from API responses
 * @param {any} apiResponse - The API response object
 * @returns {string} - Server message or fallback
 */
export const extractServerMessage = (apiResponse) => {
  // First priority: direct message field
  if (apiResponse?.message) {
    return extractMessage(apiResponse.message);
  }

  // Second priority: nested data message
  if (apiResponse?.data?.message) {
    return extractMessage(apiResponse.data.message);
  }

  // Third priority: error field
  if (apiResponse?.error) {
    return extractMessage(apiResponse.error);
  }

  // Fourth priority: response data message (for axios errors)
  if (apiResponse?.response?.data?.message) {
    return extractMessage(apiResponse.response.data.message);
  }

  // Fifth priority: check if apiResponse itself is the message
  if (typeof apiResponse === "string" && apiResponse.trim()) {
    return apiResponse.trim();
  }

  return null; // No server message found
};

/**
 * Handle API errors appropriately in components
 * @param {Error} error - The error object
 * @param {Function} showError - Function to show error message (e.g., toast.error)
 * @param {string} defaultMessage - Default error message to show
 */
export const handleApiError = (
  error,
  showError,
  defaultMessage = "خطایی رخ داده است."
) => {
  // Don't show error messages for authentication errors
  if (isAuthError(error)) {
    return;
  }

  // Extract and show the error message, prioritizing server messages
  if (typeof showError === "function") {
    const serverMessage = extractServerMessage(error);
    let finalMessage;

    if (serverMessage && serverMessage !== "خطایی رخ داده است.") {
      finalMessage = serverMessage;
    } else {
      finalMessage = extractMessage(defaultMessage);
    }

    // Ensure we're passing a string to showError
    if (typeof finalMessage === "string") {
      showError(finalMessage);
    } else {
      showError("خطایی رخ داده است.");
    }
  }
};

/**
 * Enhanced error handler for async operations in components
 * @param {Function} asyncOperation - The async operation to execute
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback (optional)
 * @param {Function} onFinally - Finally callback (optional)
 */
export const handleAsyncOperation = async (
  asyncOperation,
  onSuccess,
  onError,
  onFinally
) => {
  try {
    const result = await asyncOperation();
    if (onSuccess) {
      onSuccess(result);
    }
    return result;
  } catch (error) {
    // Don't handle auth errors - they're handled by axios interceptor
    if (!isAuthError(error) && onError) {
      onError(error);
    }
  } finally {
    if (onFinally) {
      onFinally();
    }
  }
};
